import store from './store/store'
import Vue from 'vue';
import Router from 'vue-router';

import methods from './methods.js';
import { LIST_ROUTES, TAB_LABELS, detailEntryFor, redirectForRetiredList } from './listViewRegistry.js';
import { consumePopState, installHistory } from './backInterceptor.js';
import navTrail from './navTrail.js';
import { installStaleBundleReload } from './staleBundle.js';

Vue.use(Router);

// Batch 5, Warnings & Approvals: the follow-up settings guard (showSettings) plus role name Admin.
function isAdminWithSettings() {
  const profile = store.getters['auth/getMyProfile']
  return !!(profile && profile.role && profile.role.showSettings && profile.role.roleName === 'Admin')
}

const routes = [
    {
      path: '/dashboard',
      name: 'dashboard1',
      exact: true,
      component: () => import('./components/Dashboard.vue')
    },
    {
      path: '/test',
      name: 'test',
      component: () => import('./components/Test.vue')
    },
    // THIS IS FOR MBP
    {
      path: '/',
      name: 'dashboard',
      exact: true,
      component: () => import('./mbp/dashboard/Dashboard.vue')
    },
    {
      // The user picks their own dashboard widgets (item 4 W3, decision 8). Open to everyone:
      // every call it makes is about the signed-in user and none of them takes a user id, so
      // there is nothing here to guard.
      path: '/myWidgets',
      name: 'myWidgets',
      component: () => import('./mbp/dashboard/MyWidgets.vue')
    },
    {
      // The admin composes a widget for the catalogue (item 4 W5, contract §3 and §7). Unlike
      // /myWidgets above this is gated: what is composed here is offered to every other user,
      // so it is not a decision about the composer's own dashboard.
      //
      // Gated on the permission code, the way the report menu is - the only gate in this
      // codebase that actually works. The showAdmin guards on /users and /roles are commented
      // out and gate nothing, so they are not the pattern to copy; the live showSettings
      // guards on /customerSetting/* are.
      path: '/widgetBuilder',
      name: 'widgetBuilder',
      beforeEnter (to, from, next) {
        const held = (store.getters['auth/getUserPermissions'] || [])
          .find(a => a && a.permission && a.permission.permissionCode === 'Dashboard-ComposeWidgets')
        // Held at level 0 means the role has the code but not the right: that is a refusal.
        if (held && held.permissionLevel !== 0) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/dashboard/WidgetBuilder.vue')
    },
    {
      path: '/reports/:id',
      name: 'reports',
      component: () => import('./mbp/Reports/Reports.vue'),
      // beforeEnter (to, from, next) {
      //   if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.roleName === 'Admin') {
      //     next()
      //   } else {
      //     next(false)
      //   }
      // }
    },
    {
      // Not a db_report grid: the duplicate-enquiry analysis has three lists and a
      // side-by-side comparison, so it gets its own page rather than /reports/:id.
      path: '/duplicateEnquiries',
      name: 'duplicateEnquiries',
      component: () => import('./mbp/Reports/DuplicateEnquiries.vue')
    },
    {
      // DRAFT: enquiries by branch x product type. Its own page, not a db_report grid. Gated inside the
      // page (and server-side) on Dashboard-ComposeWidgets, like the duplicate-enquiries page, because
      // permissions are not loaded yet on a hard refresh and a beforeEnter would refuse the route.
      path: '/branchProductAnalysis',
      name: 'branchProductAnalysis',
      component: () => import('./mbp/Reports/BranchProductAnalysis.vue')
    },
    {
      path: '/customerSetting/Types',
      name: 'Types',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/Types.vue')
    },
    {
      path: '/customerSetting/Industry',
      name: 'Industry',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/Industry.vue')
    },
    {
      path: '/customerSetting/JobTitles',
      name: 'JobTitles',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/JobTitles.vue')
    },
    {
      path: '/customerSetting/Salutations',
      name: 'Salutations',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/Salutations.vue')
    },
    {
      path: '/customerSetting/SalesExecutives',
      name: 'SalesExecutives',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/SalesExecutives.vue')
    },
    {
      path: '/customerSetting/Regions',
      name: 'Regions',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/Regions.vue')
    },
    {
      path: '/customerSetting/PostalAreas',
      name: 'Postal Areas',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/PostalAreas.vue')
    },
    {
      path: '/customerSetting/citiesTowns',
      name: 'citiesTowns',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/citiesTowns/CitiesTowns.vue')
    },
    {
      path: '/customerSetting/jobFunctions',
      name: 'jobFunctions',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/customerSetting/jobFunctions/JobFunctions.vue')
    },
    {
      path: '/customerSetting/Rebates',
      name: 'Rebates',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showAdmin) {
          next()
        } else { next(false) }
      },
      component: () => import('./mbp/customerSetting/Rebates.vue')
    },
    {
      // Item 4 follow-up (04/10/2026): "Active-quote comparison shown from" and the snapshot retention.
      path: '/setting/dashboard',
      name: 'dashboardSettings',
      beforeEnter (to, from, next) {
        const profile = store.getters['auth/getMyProfile']
        if (profile && profile.role && (profile.role.showSettings || profile.role.showAdmin)) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/dashboard/ActiveQuoteCompareSettings.vue')
    },
    {
      path: '/setting/mbpDepartments',
      name: 'mbpDepartments',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/Settings/MBPDepartments/MBPDepartments.vue')
    },
    {
      path: '/actualSales',
      name: 'actualSales',
      component: () => import('./mbp/actualSales/ActualSales.vue')
    },
    {
      path: '/salesForcast',
      name: 'salesForcast',
      component: () => import('./mbp/salesForcast/SalesForcast.vue')
    },
    {
      path: '/salesData',
      name: 'salesData',
      component: () => import('./mbp/salesData/SalesData.vue')
    },
    {
      path: '/customerApprovals',
      name: 'custApprovals',
      component: () => import('./mbp/customers/customerApproval.vue')
    },
    {
      path: '/videos',
      name: 'videos',
      component: () => import('./mbp/videos/Videos.vue')
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('./mbp/customers/Customers.vue')
    },
    {
      path: '/customers/:custid',
      component: () => import('./mbp/customers/CustomerTabsDetails.vue'),
      children: [
        {
          path: '/customers/:custid/detail', ///
          name: 'customersChildDetail',
          component: () => import('./mbp/customers/customer-info/CustomerDetail-info.vue')
        },
        {
          path: '/customers/:custid/contacts',
          name: 'customersChildContacts',
          component: () => import('./mbp/customers/customer-contacts/CustomerDetail-contacts.vue')
        },
        {
          path: '/customers/:custid/linkedaccounts',
          name: 'customersChildLinkedaccounts',
          component: () => import('./mbp/customers/customer-linkedAccounts/CustomerDetail-LinkedAccounts.vue')
        },
        {
          path: '/customers/:custid/notes',
          name: 'customersChildNotes',
          component: () => import('./mbp/customers/customer-notes/CustomerDetail-notes.vue')
        },
        {
          path: '/customers/:custid/activities',
          name: 'customersChildActivities',
          component: () => import('./mbp/customers/customer-activities/CustomerDetail-customerActivities.vue')
        },
        {
          path: '/customers/:custid/quotations',
          name: 'customersChildQuotations',
          component: () => import('./mbp/customers/customer-quotations/CustomerDetail-customerQuotations.vue')
        },
        {
          path: '/customers/:custid/attachments',
          name: 'customersChildAttachments',
          component: () => import('./mbp/customers/customer-attachments/CustomerDetail-customerAttachments.vue')
        },
        {
          path: '/customers/:custid/sales',
          name: 'customersChildSales',
          component: () => import('./mbp/customers/customer-sales-stats/CustomerDetail-salesStats.vue')
        },
        {
          path: '/customers/:custid/history',
          name: 'customersChildHistory',
          component: () => import('./mbp/customers/customer-history/CustomerDetail-History.vue')
        }
      ]
    },
    {
      path: '/contacts',
      name: 'contacts',
      // Contacts (old), retired 2026-09-13 - see RETIRED_LIST_ROUTES.
      redirect: to => redirectForRetiredList('contacts', to)
    },
    {
      name: 'contactDetails',
      path: '/contacts/:contactId',
      component: () => import('./mbp/contacts/contactTabDetails.vue'),
      children: [
        {
          path: '/contacts/:contactId/detail',
          name: 'contactsChildDetail',
          component: () => import('./mbp/contacts/detailsTab/contactDetails.vue')
        },
        // {
        //   path: '/contacts/:contactId/history',
        //   component: () => import('./mbp/contacts/historyTab/contactHistory.vue')
        // }
      ]
    },
    {
      path: '/newContacts',
      name: 'new contacts',
      component: () => import('./mbp/newContacts/NewContacts.vue')
    },
    {
      path: '/branches',
      name: 'branches',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/Settings/MBPBranches.vue')
    },
    {
      path: '/tags',
      name: 'tags',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/tags/tags.vue')
    },
    {
      path: '/opportunitySetting/Status',
      name: 'Status',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/opportunitySetting/Status.vue')
    },
    {
      path: '/opportunitySetting/ProjectTypes',
      name: 'Project Types',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/opportunitySetting/ProjectTypes.vue')
    },
    {
      path: '/opportunitySetting/ProductTypes',
      name: 'Product Types',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/opportunitySetting/ProductTypes.vue')
    },
    {
      path: '/opportunitySetting/QuotationGrades',
      name: 'QuotationGrades',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/opportunitySetting/QuotationGrades/QuotationGrades.vue')
    },
    {
      path: '/quoteSetting/QuoteTypes',
      name: 'Quote Types',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/quoteSetting/QuoteTypes.vue')
    },
    {
      path: '/quoteSetting/Manufacturers',
      name: 'Manufacturers',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/quoteSetting/Manufacturers.vue')
    },
    {
      path: '/quoteSetting/QuoteStatus',
      name: 'Quote Status',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/quoteSetting/QuoteStatus.vue')
    },
    {
      path: '/quoteSetting/LostQuoteNotes',
      name: 'LostQuoteNotes',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/quoteSetting/LostQuoteNotes.vue')
    },
    {
      path: '/activitySetting/ActivityResults',
      name: 'ActivityResults',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/activitySetting/activityResults/activityResults.vue')
    },
    {
      path: '/activitySetting/ActivityStatus',
      name: 'ActivityStatus',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/activitySetting/activityStatuses/activityStatuses.vue')
    },
    {
      path: '/activitySetting/ActivityTypes',
      name: 'ActivityTypes',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/activitySetting/activityTypes/activityTypes.vue')
    },
    {
      path: '/activitySetting/ReminderTypes',
      name: 'ReminderTypes',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/activitySetting/reminderTypes/reminderTypes.vue')
    },
    {
      // Batch 5: Admin only (role name Admin, as the backend checks), on top of the settings guard
      path: '/warningsApprovals/Settings',
      name: 'WarningsSettings',
      beforeEnter (to, from, next) {
        if (isAdminWithSettings()) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/warningsApprovals/WarningsSettings.vue')
    },
    {
      path: '/warningsApprovals/Log',
      name: 'WarningsLog',
      beforeEnter (to, from, next) {
        if (isAdminWithSettings()) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/warningsApprovals/WarningsLog.vue')
    },
    {
      path: '/followupSetting/Settings',
      name: 'FollowupSettings',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/followupSetting/FollowupSettings.vue')
    },
    {
      path: '/followupSetting/Steps',
      name: 'FollowupSteps',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/followupSetting/followupSteps/followupSteps.vue')
    },
    {
      path: '/followupSetting/BankHolidays',
      name: 'BankHolidays',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/followupSetting/bankHolidays/bankHolidays.vue')
    },
    {
      path: '/followupSetting/ManualEnrolment',
      name: 'ManualEnrolment',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/followupSetting/ManualEnrolment.vue')
    },
    {
      path: '/enquiries',
      name: 'enquiries',
      component: () => import('./mbp/opportunities/Enquiries.vue')
    },
    {
      path: '/enquiries/:custid',
      component: () => import('./mbp/opportunities/OppotunityTabsDetails.vue'),
      children: [
        {
          path: '/enquiries/:custid/detail', ///
          name: 'enquiriesChildDetail',
          component: () => import('./mbp/opportunities/infoTab/OppotunityDetail-info.vue')
        },
        {
          path: '/enquiries/:custid/quotations',
          name: 'enquiriesChildQuotations',
          component: () => import('./mbp/opportunities/quotationsTab/OppotunityDetail-AllQuotations.vue')
        },
        {
          path: '/enquiries/:custid/activities',
          name: 'enquiriesChildActivities',
          component: () => import('./mbp/opportunities/activitiesTab/OppotunityDetail-AllActivities .vue')
        },
        {
          path: '/enquiries/:custid/notes',
          name: 'enquiriesChildNotes',
          component: () => import('./mbp/opportunities/notesTab/OppotunityDetail-Notes.vue')
        },
        {
          path: '/enquiries/:custid/attachments',
          name: 'enquiriesChildAttachments',
          component: () => import('./mbp/opportunities/attachmentsTab/OppotunityDetail-AllAttachments.vue')
        },
        {
          path: '/enquiries/:custid/History',
          name: 'enquiriesChildHistory',
          component: () => import('./mbp/opportunities/historyTab/OppotunityDetail-history.vue')
        },
        {
          path: '/enquiries/:custid/followup',
          name: 'oppFollowup',
          component: () => import('./mbp/opportunities/followupTab/OpportunityDetail-followup.vue')
        }
      ]
    },
    {
      path: '/quotations',
      name: 'quotations',
      component: () => import('./mbp/quotations/Quotations.vue')
    },
    {
      path: '/quotations/:quoteVerId',
      component: () => import('./mbp/quotations/quotationTabsDetails.vue'),
      children: [
        {
          path: '/quotations/:quoteVerId/detail',
          name: 'quotationsChildDetail',
          component: () => import('./mbp/quotations/detailsTab/detailsTab.vue')
        },
        {
          path: '/quotations/:quoteVerId/versions',
          name: 'quotationsChildVersions',
          component: () => import('./mbp/quotations/versionsTab/versionsTab.vue')
        },
        {
          path: '/quotations/:quoteVerId/activities',
          name: 'quotationsChildActivities',
          component: () => import('./mbp/quotations/activitiesTab/activitiesTab.vue')
        },
        {
          path: '/quotations/:quoteVerId/attachments',
          name: 'quotationsChildAttachments',
          component: () => import('./mbp/quotations/attachmentsTab/attachmentsTab.vue')
        },
        {
          path: '/quotations/:quoteVerId/history',
          name: 'quotationsChildHistory',
          component: () => import('./mbp/quotations/historyTab/historyTab.vue')
        }
      ]
    },
    {
      path: '/myActivities',
      name: 'myactivities',
      // Activities (old), retired 2026-09-13 - see RETIRED_LIST_ROUTES.
      redirect: to => redirectForRetiredList('myactivities', to)
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('./mbp/tasks/Tasks.vue')
    },
    {
      path: '/taskPage/new',
      name: 'taskPageNew',
      component: () => import('./mbp/tasks/TaskFormPage.vue')
    },
    {
      path: '/taskPage/:activityid',
      name: 'taskPage',
      component: () => import('./mbp/tasks/TaskFormPage.vue')
    },
    {
      path: '/activities',
      name: 'activities',
      component: () => import('./mbp/myActivities/MyActivities.vue')
    },
    {
      // P7 part 2: tasks and events in one list
      path: '/allActivities',
      name: 'allActivities',
      component: () => import('./mbp/allActivities/AllActivities.vue')
    },
    {
      // The Follow up page (Mohammed 04/10/2026): one page, two tabs. Open to everyone, as the two
      // pages it replaces were - the server shows each user their own and Admin all.
      path: '/followUp',
      component: () => import('./mbp/followupOutbox/FollowUp.vue'),
      children: [
        { path: '', redirect: { name: 'followUpGoingOut' } },
        {
          // item (b), batch 3: automatic follow-up emails going out in the next few working days
          path: 'goingOut',
          name: 'followUpGoingOut',
          component: () => import('./mbp/followupOutbox/EmailsGoingOut.vue')
        },
        {
          // item (e), batch 3: the follow-up emails that went
          path: 'sent',
          name: 'followUpSent',
          component: () => import('./mbp/followupOutbox/SentFollowupEmails.vue')
        }
      ]
    },
    {
      // The old pages' addresses, kept for bookmarks and links: each lands on its tab.
      path: '/emailsGoingOut',
      name: 'emailsGoingOut',
      redirect: to => ({ name: 'followUpGoingOut', query: to.query })
    },
    {
      path: '/sentFollowupEmails',
      name: 'sentFollowupEmails',
      redirect: to => ({ name: 'followUpSent', query: to.query })
    },
    {
      // B3-8: the approval page opened from the approval email (the server checks who may answer)
      path: '/followupApproval/:followupId',
      name: 'followupApproval',
      component: () => import('./mbp/followupApproval/FollowupApproval.vue')
    },
    {
      // B3-8: Admin's review of the approval answers (edits monthly)
      path: '/followupSetting/ApprovalFeedback',
      name: 'FollowupApprovalFeedback',
      beforeEnter (to, from, next) {
        if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showSettings) {
          next()
        } else {
          next(false)
        }
      },
      component: () => import('./mbp/followupApproval/ApprovalFeedback.vue')
    },
    {
      path: '/activities/:activityId',
      component: () => import('./mbp/activities/activityTabsDetails.vue'),
      children: [
        {
          path: '/activities/:activityId/detail',
          name: 'activitiesChildDetail',
          component: () => import('./mbp/activities/detailsTab/activityDetails.vue')
        },
        {
          path: '/activities/:activityId/history',
          name: 'activitiesChildHistory',
          component: () => import('./mbp/activities/historyTab/activityHistory.vue')
        }
      ]
    },
    {
      path: '/signup',
      name: 'Signup',
      component: () => import('./mbp/auth/Signup.vue')
    },
    {
      path: '/login',
      name: 'mbpLogin',
      component: () => import('./mbp/auth/Login.vue')
    },
    {
      path: '/users',
      name: 'users',
      // beforeEnter (to, from, next) {
      //   if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showAdmin) {
      //     next()
      //   } else {
      //     next(false)
      //   }
      // },
      component: () => import('./mbp/users/users.vue')
    },
    {
      path: '/activityPage/:activityid',
      name: 'activityPage',
      // beforeEnter (to, from, next) {
      //   if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showAdmin) {
      //     next()
      //   } else {
      //     next(false)
      //   }
      // },
      component: () => import('./mbp/activities/ActivityFormPage.vue')
    },
    {
      path: '/users/:userid',
      name: 'user',
      component: () => import('./mbp/users/UserTabsDetails.vue'),
      children: [
        {
          path: '/users/:userid/detail',
          name: 'usersChildDetail',
          component: () => import('./mbp/users/user_info/UserInfo.vue')
        },
        {
          path: '/users/:userid/history',
          name: 'usersChildHistory',
          component: () => import('./mbp/users/user_history/UserHistory.vue')
        },
        {
          path: '/users/:userid/dashboard',
          name: 'usersChildDashboard',
          component: () => import('./mbp/users/user_dashboard/UserDashboard.vue')
        },
        {
          path: '/users/:userid/widgets',
          name: 'usersChildWidgets',
          component: () => import('./mbp/users/user_widgets/UserWidgets.vue')
        }
      ]
    },
    {
      path: '/roles',
      name: 'roles',
      // beforeEnter (to, from, next) {
      //   if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showAdmin) {
      //     next()
      //   } else {
      //     next(false)
      //   }
      // },
      component: () => import('./mbp/roles/Roles.vue')
    },
    {
      path: '/supportCases',
      name: 'Support Cases',
      // beforeEnter (to, from, next) {
      //   if (store.getters['auth/getMyProfile'] && store.getters['auth/getMyProfile'].role && store.getters['auth/getMyProfile'].role.showAdmin) {
      //     next()
      //   } else {
      //     next(false)
      //   }
      // },
      component: () => import('./mbp/supportCase/SupportCase.vue')
    },
    {
      // W7 T7: the page SupportCaseTable's View button has always pointed at. One tab, so no
      // tab shell - the Roles pattern's TabMenu earns its keep only with a second tab.
      path: '/supportCases/:caseid/detail',
      // Not a "…Child…" name on purpose: /supportCases is not in listViewRegistry, so this
      // page reaches the breadcrumb through the path-split + humanise() fallback (W7 T1),
      // exactly as its own list does.
      name: 'supportCaseDetail',
      component: () => import('./mbp/supportCase/SupportCaseDetail.vue')
    },
    {
      path: '/roles/:roleid',
      name: 'user',
      component: () => import('./mbp/roles/RoleTabsDetails.vue'),
      children: [
        {
          path: '/roles/:roleid/detail',
          name: 'rolesChildDetail',
          component: () => import('./mbp/roles/infoTab/RoleInfo.vue')
        },
        {
          path: '/roles/:roleid/permissions',
          name: 'rolesChildPermissions',
          component: () => import('./mbp/roles/permissionsTab/RolePermissions.vue')
        }
      ]
    },



    // Demo
    // {
    //   path: '/formlayout',
    //   name: 'formlayout',
    //   component: () => import('./components/FormLayoutDemo.vue')
    // },
    // {
    //   path: '/input',
    //   name: 'input',
    //   component: () => import('./components/InputDemo.vue')
    // },
    // {
    //   path: '/button',
    //   name: 'button',
    //   component: () => import('./components/ButtonDemo.vue')
    // },
    // {
    //   path: '/table',
    //   name: 'table',
    //   component: () => import('./components/TableDemo.vue')
    // },
    // {
    //   path: '/list',
    //   name: 'list',
    //   component: () => import('./components/ListDemo.vue')
    // },
    // {
    //   path: '/tree',
    //   name: 'tree',
    //   component: () => import('./components/TreeDemo.vue')
    // },
    // {
    //   path: '/panel',
    //   name: 'panel',
    //   component: () => import('./components/PanelsDemo.vue')
    // },
    // {
    //   path: '/overlay',
    //   name: 'overlay',
    //   component: () => import('./components/OverlayDemo.vue')
    // },
    // {
    //   path: '/menu',
    //   component: () => import('./components/MenuDemo.vue'),
    //   children: [{
    //     path: '',
    //     component: () => import('./components/menu/PersonalDemo.vue')
    //   },
    //   {
    //     path: '/menu/seat',
    //     component: () => import('./components/menu/SeatDemo.vue')
    //   },
    //   {
    //     path: '/menu/payment',
    //     component: () => import('./components/menu/PaymentDemo.vue')
    //   },
    //   {
    //     path: '/menu/confirmation',
    //     component: () => import('./components/menu/ConfirmationDemo.vue')
    //   }]
    // },
    // {
    //   path: '/messages',
    //   name: 'messages',
    //   component: () => import('./components/MessagesDemo.vue')
    // },
    // {
    //   path: '/file',
    //   name: 'file',
    //   component: () => import('./components/FileDemo.vue')
    // },
    // {
    //   path: '/chart',
    //   name: 'chart',
    //   component: () => import('./components/ChartsDemo.vue')
    // },
    // {
    //   path: '/misc',
    //   name: 'misc',
    //   component: () => import('./components/MiscDemo.vue')
    // },
    // {
    //   path: '/icons',
    //   name: 'icons',
    //   component: () => import('./utilities/Icons.vue')
    // },
    // {
    //   path: '/widgets',
    //   name: 'widgets',
    //   component: () => import('./utilities/Widgets.vue')
    // },
    // {
    //   path: '/grid',
    //   name: 'grid',
    //   component: () => import('./utilities/GridDemo.vue')
    // },
    // {
    //   path: '/spacing',
    //   name: 'spacing',
    //   component: () => import('./utilities/SpacingDemo.vue')
    // },
    // {
    //   path: '/elevation',
    //   name: 'elevation',
    //   component: () => import('./utilities/ElevationDemo.vue')
    // },
    // {
    //   path: '/typography',
    //   name: 'typography',
    //   component: () => import('./utilities/Typography.vue')
    // },
    // {
    //   path: '/display',
    //   name: 'display',
    //   component: () => import('./utilities/DisplayDemo.vue')
    // },
    // {
    //   path: '/flexbox',
    //   name: 'flexbox',
    //   component: () => import('./utilities/FlexBoxDemo.vue')
    // },
    // {
    //   path: '/text',
    //   name: 'text',
    //   component: () => import('./utilities/TextDemo.vue')
    // },
    // {
    //   path: '/crud',
    //   name: 'crud',
    //   component: () => import('./pages/CrudDemo.vue')
    // },
    // {
    //   path: '/calendar',
    //   name: 'calendar',
    //   component: () => import('./pages/CalendarDemo.vue')
    // },
    // {
    //   path: '/invoice',
    //   name: 'invoice',
    //   component: () => import('./pages/Invoice.vue')
    // },
    // {
    //   path: '/help',
    //   name: 'help',
    //   component: () => import('./pages/Help.vue')
    // },
    // {
    //   path: '/empty',
    //   name: 'empty',
    //   component: () => import('./components/EmptyPage.vue')
    // },
    // {
    //   path: '/documentation',
    //   name: 'documentation',
    //   component: () => import('./components/Documentation.vue')
    // },
];

// Stamp breadcrumb metadata onto the route table from the registry, rather than
// repeating a meta block on each of the ~60 route literals above. Walks
// children too, so detail tabs get their own crumb.
function applyBreadcrumbMeta(routeList) {
  routeList.forEach(route => {
    const list = LIST_ROUTES[route.name];
    if (list) {
      route.meta = Object.assign({}, route.meta, {
        breadcrumb: list.label,
        listRoute: route.name,
        storeModule: list.storeModule
      });
    }

    const detail = detailEntryFor(route.name);
    if (detail) {
      route.meta = Object.assign({}, route.meta, {
        breadcrumb: TAB_LABELS[route.name] || detail.label,
        recordLabel: detail.label,
        listRoute: detail.list
      });
    }

    if (route.children && route.children.length) {
      applyBreadcrumbMeta(route.children);
    }
  });
}

applyBreadcrumbMeta(routes);

// Returns the scroll offset a list stored on its way out, or 0.
function storedScrollY(routeName) {
  const list = LIST_ROUTES[routeName];
  if (!list) return 0;
  const getter = store.getters[`${list.storeModule}/getScrollY`];
  return getter || 0;
}

let router = new Router({
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    // A genuine popstate the router handled itself - honour the browser.
    if (savedPosition) {
      return savedPosition;
    }

    // Returning to a list that has a stored offset: leave the scroll alone.
    // The list restores it itself once its lazy grid has rendered rows
    // (ListViewStateMixin.restoreListScroll); scrolling here would just be
    // undone when the grid replaces the placeholder rows.
    if (storedScrollY(to.name)) {
      return false;
    }

    return { x: 0, y: 0 };
  }
});

// A tab opened before a deploy asks for chunk files the deploy removed; open the page as a full
// page load instead of doing nothing (2026-09-28, the customer page in the client demo).
installStaleBundleReload(router);



// Back is plain browser history (item 2, Mohammed's decision 2026-09-27). installHistory keeps
// the Back/Forward counter in every entry, turns a tab switch's push into a replace when the
// user's "Back button ignores tabs on records" setting is ON, and hands the trail stamped on the
// entry a Back/Forward lands on to navTrail, which puts it back once the navigation completes.
// See backInterceptor.js and navTrail.js.
installHistory(window, {
  takePushAsReplace: navTrail.takePushAsReplace,
  onPopState: navTrail.popstate
})



router.beforeEach((to, from, next) => {
  // Read once per navigation - see consumePopState.
  const popState = consumePopState(window)

  navTrail.beforeEach({
    to,
    from,
    popState,
    initialState: window.history.state,
    profile: store.getters['auth/getMyProfile']
  })

  // the page asked for, so the login can return to it; the login and sign-up pages themselves are never it, or a
  // link from an email (e.g. the follow-up approval page) would land on the dashboard after signing in
  if (to.name !== 'mbpLogin' && to.name !== 'Signup') store.dispatch('setLastPage', to.fullPath)

  if (
    to.name !== 'Signup' && 
    to.name !== 'mbpLogin' && (
      store.getters['auth/getMyProfile'] === null ||
      store.getters['auth/getMyProfile'].id === null
    )
  ) {
    
    next({ name: 'mbpLogin' })
  } else if (
    (
      to.name === 'Signup' ||
      to.name === 'mbpLogin'
    ) &&
    (
      store.getters['auth/getMyProfile'] &&
      store.getters['auth/getMyProfile'].id
    )
  ) {
    
    next({ name: 'dashboard' })
  } 
  else {
    
    next(true)
  }
  
  if(!methods.matchStartingSubString(to.name, from.name))
  {
    store.dispatch('setLastPathName', from.name)
  }

  if (to.query && to.query.logout) {
    localStorage.clear();
  }
 
})

// router.beforeEach((to, from, next) => {
//   
//   if (to.path !== '/login' && !store.getters['auth/getMyProfile']) next({ path: '/login' })
//   else next()
// })

// A Back or Forward onto an entry that carries a trail puts that trail back - see navTrail.js.
router.afterEach(to => {
  const restored = navTrail.afterEach(to)
  if (restored) store.dispatch('setBreadcrumbTrail', restored)
})

export default router
